<!-- lib/components/DocumentMenu.svelte -->
<script>
  export let parsedTOC = {};
  export let activeSubPage;
  export let mobile = false;
  export let navigateToPage;
  export let baseDocumentUrl;
  export let documentVersion;
  export let menuTitle = "";

  const getActiveLabels = (toc, activePage) => {
    if (!activePage) {
      return ["", ""];
    }

    let page;
    let category;
    const found = Object.entries(toc).find(([header, data]) => {
      return Object.entries(data.pages).find(([pageLink, label]) => {
        if (pageLink === activePage) {
          page = label;
          return true;
        }
      });
    });
    category = found?.[0];
    return [category, page];
  };

  const handleLinkClick = (event, pageLink) => {
    navigateToPage(pageLink);
    // Scroll to top when navigating
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  let activeCategory, activeLabel;
  $: {
    [activeCategory, activeLabel] = getActiveLabels(parsedTOC, activeSubPage);
  }
</script>

<div class="document-menu" class:mobile>
  <nav class="menu-items" aria-label="Document navigation">
    {#each Object.entries(parsedTOC) as [header, data]}
      <div class="menu-category">
        <h3 class="category-title">{header}</h3>
      </div>
      <div class="category-pages">
        {#each Object.entries(data.pages) as [pageLink, label]}
          <a
            href={`${baseDocumentUrl}${pageLink}`}
            class="menu-link"
            class:active={pageLink === activeSubPage}
            on:click={(e) => handleLinkClick(e, pageLink)}
          >
            {label}
          </a>
        {/each}
      </div>
    {/each}
  </nav>
</div>

<style lang="scss">
  .document-menu {
    width: 100%;

    &:not(.mobile) {
      position: sticky;
      top: 2rem;
    }

    &.mobile {
      width: 100%;
    }
  }

  .menu-items {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
  }

  .menu-category {
    margin-top: 1rem;
    width: 100%;

    &:first-child {
      margin-top: 0;
    }
  }

  .category-title {
    color: var(--text-color1);
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    line-height: 1.4;
    width: 100%;
  }

  .category-pages {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    margin-bottom: 1rem;
    width: 100%;
  }

  .menu-link {
    color: var(--text-color3);
    text-decoration: none;
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.5;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    transition: all 0.3s ease;
    position: relative;
    display: block;
    width: 100%;
    box-sizing: border-box;

    &:hover {
      color: var(--text-color1);
      background: rgba(255, 255, 255, 0.05);
      transform: translateX(2px);
    }

    &:focus {
      color: var(--text-color1);
      background: rgba(255, 255, 255, 0.05);
      outline: 2px solid var(--text-color1);
      outline-offset: 2px;
    }

    &.active {
      color: var(--text-color1);
      background: rgba(210, 165, 82, 0.15);
      font-weight: 600;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 60%;
        background: var(--text-color1);
        border-radius: 2px;
        box-shadow: 0 0 8px rgba(210, 165, 82, 0.3);
      }

      &:hover {
        background: rgba(210, 165, 82, 0.2);
        transform: translateX(2px);
      }
    }
  }

  // Mobile specific styles
  .mobile {
    .menu-items {
      width: 100%;
      max-width: 100%;
    }

    .menu-category {
      width: 100%;
    }

    .category-title {
      width: 100%;
      text-align: left;
      padding: 0;
    }

    .category-pages {
      width: 100%;
    }

    .menu-link {
      padding: 0.5rem 0.75rem;
      width: 100%;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        transform: translateX(3px);
        background: rgba(255, 255, 255, 0.08);
        white-space: normal;
        word-wrap: break-word;
      }

      &.active {
        background: rgba(210, 165, 82, 0.2);
        white-space: normal;
        word-wrap: break-word;

        &::before {
          left: 0;
          width: 3px;
          height: 60%;
        }
      }
    }
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    .document-menu:not(.mobile) {
      position: relative;
      top: auto;
    }

    .category-title {
      font-size: 1rem;
    }

    .menu-link {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 480px) {
    .mobile {
      .category-title {
        font-size: 0.95rem;
        margin-bottom: 0.375rem;
      }

      .menu-link {
        font-size: 0.875rem;
        padding: 0.5rem 0.625rem;
        line-height: 1.4;
      }
    }
  }

  @media (max-width: 360px) {
    .mobile {
      .category-title {
        font-size: 0.9rem;
      }

      .menu-link {
        font-size: 0.8125rem;
        padding: 0.4375rem 0.5rem;
      }
    }
  }
</style>
